import React, { useEffect, useId, useRef } from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { TextField } from '@mui/material'

const customRules = new Map()

const EMAIL_RE =
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

function parseRule(rule) {
  const raw = String(rule)
  const colon = raw.indexOf(':')
  if (colon === -1) return { name: raw.trim(), param: undefined }
  return {
    name: raw.slice(0, colon).trim(),
    param: raw.slice(colon + 1).trim(),
  }
}

function isEmptyValue(value) {
  return value == null || String(value).trim() === ''
}

export function evaluateValidator(rule, value) {
  const { name, param } = parseRule(rule)
  if (name !== 'required' && isEmptyValue(value)) return true

  switch (name) {
    case 'required':
      return !isEmptyValue(value)
    case 'isEmail':
      return EMAIL_RE.test(String(value))
    case 'isNumber':
    case 'isFloat':
      return Number.isFinite(Number(value))
    case 'minStringLength':
      return String(value).length >= Number(param)
    case 'maxStringLength':
      return String(value).length <= Number(param)
    case 'matchRegexp':
      return new RegExp(param).test(String(value))
    default: {
      const custom = customRules.get(name)
      if (!custom) return true
      return Boolean(custom(value, param))
    }
  }
}

function firstError(validators = [], errorMessages = [], value) {
  for (let i = 0; i < validators.length; i += 1) {
    if (!evaluateValidator(validators[i], value)) {
      return errorMessages[i] || 'Invalid'
    }
  }
  return null
}

function formMode(instantValidate) {
  // onTouched: first errors after blur/submit, then on change. Avoids
  // validating (and shifting helper text) on every keystroke.
  return instantValidate ? 'onTouched' : 'onSubmit'
}

export function ValidatorForm({
  onSubmit,
  instantValidate = true,
  debounceTime: _debounceTime,
  onError: _onError,
  children,
  ...formProps
}) {
  const methods = useForm({
    mode: formMode(instantValidate),
  })

  return (
    <FormProvider {...methods}>
      {/*
        handleSubmit always cancels the native submit. `method="post"` only
        matters when something bypasses React (HTMLFormElement.submit() from an
        extension, password manager or automation skips the submit event): the
        HTML default is GET, which would serialize every field, passwords
        included, into the address bar.
      */}
      <form
        noValidate
        method="post"
        {...formProps}
        onSubmit={methods.handleSubmit((_values, event) => {
          onSubmit?.(event)
        })}
      >
        {children}
      </form>
    </FormProvider>
  )
}

ValidatorForm.addValidationRule = (name, handler) => {
  customRules.set(name, handler)
}

ValidatorForm.removeValidationRule = (name) => {
  customRules.delete(name)
}

function TextValidatorInner({
  name,
  value,
  onChange,
  onBlur,
  validators = [],
  errorMessages = [],
  error,
  helperText,
  children,
  ...textFieldProps
}) {
  const reactId = useId()
  const fieldName = name || `validator-${reactId}`
  const form = useFormContext()
  const validatorsRef = useRef(validators)
  const errorMessagesRef = useRef(errorMessages)
  validatorsRef.current = validators
  errorMessagesRef.current = errorMessages

  useEffect(() => {
    if (!form) return
    const current = form.getValues(fieldName)
    if ((value ?? '') === (current ?? '')) return
    form.setValue(fieldName, value ?? '', {
      shouldValidate: false,
      shouldDirty: false,
    })
  }, [fieldName, form, value])

  return (
    <Controller
      name={fieldName}
      control={form.control}
      defaultValue={value ?? ''}
      rules={{
        validate: (current) =>
          firstError(
            validatorsRef.current,
            errorMessagesRef.current,
            current,
          ) || true,
      }}
      render={({ field, fieldState  }) => (
        <TextField
          {...textFieldProps}
          name={field.name}
          inputRef={field.ref}
          value={value ?? ''}
          onChange={(event) => {
            field.onChange(event.target.value)
            onChange?.(event)
          }}
          onBlur={(event) => {
            field.onBlur()
            onBlur?.(event)
          }}
          error={Boolean(error || fieldState.error)}
          helperText={fieldState.error?.message || helperText}
        >
          {children}
        </TextField>
      )}
    />
  )
}

function StandaloneTextValidator(props) {
  const methods = useForm({ mode: formMode(true)  })
  return (
    <FormProvider {...methods}>
      <TextValidatorInner {...props} />
    </FormProvider>
  )
}

export function TextValidator(props) {
  const form = useFormContext()
  if (form) return <TextValidatorInner {...props} />
  return <StandaloneTextValidator {...props} />
}
